import Modal from "Components/molecule/modal"
import Chip from "Components/atoms/chip"
import { LambdaRun, LambdaTrackerUI } from "models/lambda-tracker"
import { formatDateTimeAmPm } from "utils/tools"

interface LambdaRunModalProps {
    run: LambdaRun | null
    lambdaName: string
    functionName: string | null
    onClose: () => void
}

const SOURCE_LABEL: Record<LambdaRun["source"], string> = {
    syncLog: "SyncLog (written by the Lambda)",
    cloudWatch: "CloudWatch Logs only (no SyncLog row)",
    schedule: "Expected schedule (no run found)",
}

// Full detail of one run: why it is yellow/red, timings, memory, cost and where to look next.
const LambdaRunModal = ({ run, lambdaName, functionName, onClose }: LambdaRunModalProps) => {
    if (!run) return null

    const rows: [string, React.ReactNode][] = [
        ["Started", formatDateTimeAmPm(run.startAt)],
        ["Finished", formatDateTimeAmPm(run.finishedAt)],
        ["Expected at", run.expectedAt ? formatDateTimeAmPm(run.expectedAt) : "-"],
        ["Late by", run.lateByMinutes !== null ? `${run.lateByMinutes} min` : "-"],
        ["Duration", LambdaTrackerUI.duration(run.durationMs)],
        ["Billed duration", run.billedDurationMs !== null ? LambdaTrackerUI.duration(run.billedDurationMs) : "Not available (estimated from the run times)"],
        ["Cold start (init)", LambdaTrackerUI.duration(run.initDurationMs)],
        ["Memory used / configured", `${LambdaTrackerUI.memory(run.maxMemoryUsedMb)} / ${LambdaTrackerUI.memory(run.memorySizeMb)}`],
        ["Cost", `${LambdaTrackerUI.cost(run.costUsd)}${run.estimated && run.costUsd !== null ? " (estimated)" : ""}`],
        ["Request ID", run.awsRequestId ? <code>{run.awsRequestId}</code> : "-"],
        ["Source", SOURCE_LABEL[run.source]],
    ]

    const logHint = functionName && run.awsRequestId
        ? `aws logs filter-log-events --log-group-name /aws/lambda/${functionName} --filter-pattern "\\"${run.awsRequestId}\\""`
        : null

    return (
        <Modal
            isOpen={!!run}
            onClose={onClose}
            title={lambdaName}
            subtitle={formatDateTimeAmPm(LambdaTrackerUI.runTime(run))}
            className="lambda-run-modal"
        >
            <div className="lambda-run-detail">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <Chip variant={LambdaTrackerUI.chipVariant(run.health)}>
                        <i className={`${LambdaTrackerUI.icon(run)} me-1`} aria-hidden />
                        {LambdaTrackerUI.statusLabel(run.status)}
                    </Chip>
                </div>

                {(run.errorMessage || run.reason) && (
                    <div className={`run-reason health-${run.health}`}>
                        <strong>{run.health === "green" ? "Note" : "Why"}</strong>
                        <pre>{run.errorMessage ?? run.reason}</pre>
                        {run.errorMessage && run.reason && run.reason !== run.errorMessage && <small>{run.reason}</small>}
                    </div>
                )}

                <table className="table table-sm mb-0">
                    <tbody>
                        {rows.map(([label, value]) => (
                            <tr key={label}>
                                <th scope="row">{label}</th>
                                <td>{value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {logHint && (
                    <div className="mt-3">
                        <small className="text-muted d-block mb-1">Full log of this run (CloudWatch):</small>
                        <pre className="log-hint">{logHint}</pre>
                    </div>
                )}
            </div>
        </Modal>
    )
}

export default LambdaRunModal
